import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Mail, Send, Eye } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const STATUS = {
    sent: 'bg-emerald-500/10 text-emerald-500', queued: 'bg-sky-500/10 text-sky-500', retrying: 'bg-amber-500/10 text-amber-500', console: 'bg-zinc-500/10 text-zinc-400',
    suppressed: 'bg-zinc-500/10 text-zinc-400', failed: 'bg-red-500/10 text-red-500',
};
const STATUS_LABEL = { sent: 'Enviado', queued: 'En cola', retrying: 'Reintentando', console: 'Consola', suppressed: 'Bloqueado (prueba)', failed: 'Falló' };

export const EmailCenterCard = () => {
    const { apiCall } = useAdminAuth();
    const [templates, setTemplates] = useState([]);
    const [selected, setSelected] = useState('password_reset');
    const [preview, setPreview] = useState(null);
    const [showText, setShowText] = useState(false);
    const [log, setLog] = useState([]);
    const [sending, setSending] = useState(false);

    const loadLog = useCallback(async () => {
        try { setLog((await apiCall('get', '/admin/email/log')).data); } catch { /* sin permiso */ }
    }, [apiCall]);

    useEffect(() => {
        apiCall('get', '/admin/email/templates').then((r) => setTemplates(r.data)).catch(() => {});
        loadLog();
    }, [apiCall, loadLog]);

    useEffect(() => {
        apiCall('get', `/admin/email/templates/${selected}/preview`).then((r) => setPreview(r.data)).catch(() => setPreview(null));
    }, [apiCall, selected]);

    const sendTest = async () => {
        setSending(true);
        try {
            const r = await apiCall('post', '/admin/email/test', { template: selected });
            toast.success(`Correo de prueba en cola para ${r.data.to}. Revisa el estado en «Últimos envíos».`);
            [2000, 8000, 25000].forEach((ms) => setTimeout(loadLog, ms));
        } catch (e) {
            toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'No se pudo enviar');
        } finally {
            setSending(false);
        }
    };

    return (
        <Card data-testid="email-center-card">
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
                <CardTitle className="text-lg flex items-center gap-2"><Mail className="w-5 h-5 text-amber-500" />Correos de AeroOps</CardTitle>
                <div className="flex items-center gap-2">
                    <Select value={selected} onValueChange={setSelected}>
                        <SelectTrigger className="w-60" data-testid="email-template-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {templates.map((t) => <SelectItem key={t.key} value={t.key} data-testid={`email-template-${t.key}`}>{t.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => setShowText(!showText)} data-testid="email-toggle-text-btn"><Eye className="w-4 h-4 mr-1" />{showText ? 'HTML' : 'Texto'}</Button>
                    <Button size="sm" onClick={sendTest} disabled={sending} data-testid="email-send-test-btn"><Send className="w-4 h-4 mr-1" />Enviar prueba</Button>
                </div>
            </CardHeader>
            <CardContent className="grid lg:grid-cols-[1fr_320px] gap-4">
                <div>
                    <p className="text-sm mb-2" data-testid="email-preview-subject"><span className="text-muted-foreground">Asunto:</span> {preview?.subject}</p>
                    {showText ? (
                        <pre className="text-xs whitespace-pre-wrap bg-muted/30 rounded-lg p-4 h-[420px] overflow-auto" data-testid="email-preview-text">{preview?.text}</pre>
                    ) : (
                        <iframe title="Vista previa" sandbox="" srcDoc={preview?.html || ''} className="w-full h-[420px] rounded-lg border border-border bg-white" data-testid="email-preview-frame" />
                    )}
                </div>
                <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Últimos envíos</p>
                    <div className="space-y-2 max-h-[440px] overflow-y-auto" data-testid="email-log-list">
                        {log.map((l) => (
                            <div key={l.id} className="text-xs border-b border-border pb-2" data-testid={`email-log-${l.id}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <span className="truncate font-medium">{l.to}</span>
                                    <Badge className={STATUS[l.status] || ''}>{STATUS_LABEL[l.status] || l.status}</Badge>
                                </div>
                                <p className="text-muted-foreground">{l.template} · {new Date(l.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })}</p>
                            </div>
                        ))}
                        {log.length === 0 && <p className="text-sm text-muted-foreground">Sin envíos todavía</p>}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
